/**
 * Issue reporting — shared types.
 *
 * There is NO severity here, and no priority either.
 *
 * Severity used to be collected from the user, but it was barred from driving routing,
 * sorting, dashboards and alerting — so it changed nothing while implying to the user
 * that it set their queue position. It has been removed rather than left as decoration.
 *
 * Priority is what actually triages an issue. It is set by an admin, is never returned
 * to the reporter, and therefore has no representation in these user-facing types —
 * that is deliberate, not an omission.
 */

export type IssueType =
  | 'bug'
  | 'performance_issue'
  | 'ui_issue'
  | 'sync_issue'
  | 'pricing_issue'
  | 'tax_calculation_issue'
  | 'feature_request'
  | 'improvement_suggestion'
  | 'other';

/**
 * Mirrors the backend IssueModule enum, which is derived from the REAL `/app/*` route
 * table — not an idealised product map. Wallets and exchanges are one unified
 * Integrations page in this app, and there is no standalone reports route.
 */
export type IssueModule =
  | 'dashboard'
  | 'portfolio'
  | 'transactions'
  | 'tax_center'
  | 'integrations'
  | 'ai_optimizer'
  | 'harvestable_losses'
  | 'rwa_assets'
  | 'zk_proofs'
  | 'residency'
  | 'api_management'
  | 'settings'
  | 'authentication'
  | 'other';

export type IssueStatus =
  | 'submitted'
  | 'pending_review'
  | 'assigned'
  | 'in_progress'
  | 'ready_for_qa'
  | 'qa_testing'
  | 'completed'
  | 'closed'
  | 'duplicate'
  | 'need_more_information'
  | 'rejected'
  | 'reopened'
  | 'cannot_reproduce';

export const ISSUE_TYPE_OPTIONS: ReadonlyArray<{ value: IssueType; label: string }> = [
  { value: 'bug', label: 'Bug' },
  { value: 'performance_issue', label: 'Performance Issue' },
  { value: 'ui_issue', label: 'UI Issue' },
  { value: 'sync_issue', label: 'Sync Issue' },
  { value: 'pricing_issue', label: 'Pricing Issue' },
  { value: 'tax_calculation_issue', label: 'Tax Calculation Issue' },
  { value: 'feature_request', label: 'Feature Request' },
  { value: 'improvement_suggestion', label: 'Improvement Suggestion' },
  { value: 'other', label: 'Other' }
];

export const MODULE_OPTIONS: ReadonlyArray<{ value: IssueModule; label: string }> = [
  { value: 'dashboard', label: 'Dashboard' },
  { value: 'portfolio', label: 'Portfolio' },
  { value: 'transactions', label: 'Transactions' },
  { value: 'tax_center', label: 'Tax Center' },
  { value: 'integrations', label: 'Integrations (Wallets & Exchanges)' },
  { value: 'ai_optimizer', label: 'AI Optimizer' },
  { value: 'harvestable_losses', label: 'Harvestable Losses' },
  { value: 'rwa_assets', label: 'RWA Assets' },
  { value: 'zk_proofs', label: 'ZK Proofs' },
  { value: 'residency', label: 'Residency' },
  { value: 'api_management', label: 'API Management' },
  { value: 'settings', label: 'Settings' },
  { value: 'authentication', label: 'Authentication' },
  { value: 'other', label: 'Other' }
];

export const STATUS_LABELS: Record<IssueStatus, string> = {
  submitted: 'Submitted',
  pending_review: 'Pending Review',
  assigned: 'Assigned',
  in_progress: 'In Progress',
  ready_for_qa: 'Ready for QA',
  qa_testing: 'QA Testing',
  completed: 'Completed',
  closed: 'Closed',
  duplicate: 'Duplicate',
  need_more_information: 'Need More Information',
  rejected: 'Rejected',
  reopened: 'Reopened',
  cannot_reproduce: 'Cannot Reproduce'
};

/** Conditional per-type context (§1.7). Only the fields for the chosen type are sent. */
export interface IssueContext {
  // Tax Calculation Issue
  tax_year?: number;
  jurisdiction?: string;
  report_id?: string;
  // Sync Issue
  connection_id?: string;
  last_sync_at?: string;
  chain?: string;
  // Pricing Issue
  asset?: string;
  date?: string;
  expected_price_source?: string;
  // Performance Issue
  occurred_at?: string;
}

export interface CreateIssueRequest {
  title: string;
  issue_type: IssueType;
  description: string;
  module?: IssueModule;
  steps_to_reproduce?: string;
  expected_behavior?: string;
  actual_behavior?: string;
  attachments?: string[];
  context?: IssueContext;
  /**
   * Display-only. Support staff see it; automated mail NEVER goes here — it goes to
   * the account email, resolved server-side. Do not repurpose this as a send target.
   */
  contact_email_hint?: string;

  // Tier 1 environment — captured silently, no consent needed.
  browser?: string;
  browser_version?: string;
  operating_system?: string;
  device?: string;
  screen_resolution?: string;
  timezone?: string;
  language?: string;
  app_version?: string;
  current_url?: string;

  // Tier 2 financial identifiers — sent ONLY when the user opts in. When
  // technical_details_consent is false these must be absent from the payload
  // entirely; the server will not backfill them.
  wallet_address?: string[];
  exchange?: string[];
  technical_details_consent?: boolean;
}

export interface UploadedAttachment {
  attachment_id: string;
  url: string;
  filename: string;
  mime: string;
  size: number;
}

export interface IssueSummary {
  _id: string;
  issue_number: string;
  title: string;
  issue_type: IssueType;
  module?: IssueModule;
  status: IssueStatus;
  reopen_count: number;
  created_at: string;
  updated_at: string;
}

export interface IssueComment {
  _id: string;
  body: string;
  author_role: 'user' | 'admin' | 'developer';
  created_at: string;
}

/**
 * Status transitions only. There is deliberately NO `comment` field: status-history
 * comments are internal triage text (assignment reasons, rejection rationale) and the
 * server does not send them to the reporter. Anything meant for the user arrives as a
 * public IssueComment. Do not add `comment` here.
 */
export interface IssueTimelineEntry {
  from_status: IssueStatus | null;
  to_status: IssueStatus;
  created_at: string;
}

export interface IssueAttachmentRef {
  attachment_id: string;
  filename: string;
  mime: string;
  size: number;
  url: string;
}

export interface IssueDetail extends IssueSummary {
  description: string;
  steps_to_reproduce?: string;
  expected_behavior?: string;
  actual_behavior?: string;
  context?: IssueContext;
  attachments?: IssueAttachmentRef[];
  timeline: IssueTimelineEntry[];
  comments: IssueComment[];
}

export interface CreatedIssue {
  _id: string;
  issue_number: string;
  status: IssueStatus;
  created_at: string;
}

/** The backend's standard { metadata, data } envelope. */
export interface ApiEnvelope<T> {
  data: T;
  metadata?: { message?: string; statusCode?: number; [k: string]: unknown };
  pagination?: { page: number; limit: number; total: number; totalPages: number };
}

// Client-side attachment limits. The server re-validates all of these — these exist
// to give fast feedback, not to enforce anything.
export const MAX_ATTACHMENTS = 5;
export const MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024;
export const MAX_ATTACHMENT_TOTAL_BYTES = 15 * 1024 * 1024;
export const ACCEPTED_MIMES = ['image/png', 'image/jpeg', 'image/webp'] as const;
